import { useQueryClient } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, Outlet, useLocation } from 'react-router';

import { ROUTES } from '@/app/routes';
import LandingPage from '@/pages/LandingPage';
import { useSessionStore } from '@/shared/store/sessionStore';
import { Button, Spinner, buttonClasses } from '@/shared/ui';

import { restoreSessionOnce } from '../model/sessionRestore';

/**
 * The gate. Every application screen sits behind it.
 *
 * It used to wrap one route — the 2FA settings page — because the rest of the
 * app was placeholders that were easier to develop unauthenticated. The screens
 * are real now, so the guard moved up to wrap the whole `AppLayout`: the public
 * surface is exactly `PUBLIC_ROUTES` and everything else, including the 404,
 * requires a session.
 *
 * This is a UX guard, not a security boundary — the real check is the backend
 * rejecting a request without a valid token. Its job is threefold: never paint a
 * shell that is about to 401, never paint one to someone who has no business
 * seeing it, and remember where they were going.
 *
 * ── THE ROOT URL IS NOT A REDIRECT ───────────────────────────────────────────
 * A visitor with no session who asks for `/` is SHOWN the landing page, at `/`,
 * rather than sent to a marketing path. That address is the one people are
 * given, paste into a chat and read off a card; bouncing it to `/welcome` would
 * mean every shared link resolves to a different URL than the one shared, and
 * the messenger preview would name the wrong page.
 *
 * It is rendered here rather than mapped as a public route on purpose. The gate
 * is the parent of the entire shell precisely so that a route added below is
 * protected unless somebody visibly moves it out; putting `/` in the public
 * branch would move the application's index route out of the guarded tree, and
 * the next person to add an index child would inherit that. One branch on one
 * exact pathname keeps the rule intact.
 *
 * ── WHERE A SIGNED-OUT USER IS SENT, AND WHY IT IS TWO PLACES ────────────────
 * `/welcome` for a visitor with no session — the mockup's `Log_in_1`, two
 * buttons, because we do not know yet whether they have an account.
 * `/login` when a session ENDED (`expiredReason` is set) — that user is mid-task
 * and wants the password box, not a landing page, and the login screen is where
 * the "you were signed out, and why" banner lives. An explicit sign-out clears
 * `expiredReason`, so it lands on /welcome.
 *
 * ── NEVER A BARE SPINNER ─────────────────────────────────────────────────────
 * `unknown` shows a brief check; `unavailable` shows what went wrong with a way
 * out. A session check that fails silently and leaves a spinner turning is the
 * one state a user cannot act on.
 */
export function RequireAuth() {
  const status = useSessionStore((state) => state.status);
  const expiredReason = useSessionStore((state) => state.expiredReason);
  const location = useLocation();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (status !== 'unknown') return;
    void restoreSessionOnce(queryClient);
  }, [status, queryClient]);

  // The landing needs no backend, so a backend that is down must not hide it.
  // Without this, the first thing a stranger sees at novixwallet.com during an
  // API blip is "we could not verify your session" — a sentence about a session
  // they do not have, on a page that is pure static content. A signed-in visitor
  // caught by the same blip gets the landing's own "sign in" button, which is a
  // better dead end than an error screen.
  const atRoot = location.pathname === '/' && expiredReason === null;

  if (status === 'unknown') return <SessionChecking />;
  if (status === 'unavailable') return atRoot ? <LandingPage /> : <SessionCheckFailed />;

  if (status !== 'authenticated') {
    // A session that EXPIRED still goes to /login with its banner: that person
    // is mid-task and wants the password box, not a page explaining what a
    // wallet is. Hence `atRoot` carrying the `expiredReason` check.
    if (atRoot) {
      return <LandingPage />;
    }

    return (
      <Navigate
        to={expiredReason === null ? ROUTES.welcome : ROUTES.login}
        replace
        // Search and hash included: a deep link into `/deposit/crypto?network=bsc`
        // has to come back with the network still chosen, or "take me back where
        // I was" quietly means "take me somewhere near where I was".
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  return <Outlet />;
}

/** The whole-page shell the two non-navigating states share. */
function GateScreen({ children }: { children: ReactNode }) {
  return (
    <div className="grid min-h-dvh bg-[var(--bg-muted)] px-5">
      <div className="mx-auto flex w-full max-w-[var(--app-max-width)] flex-col items-center justify-center gap-4 text-center">
        {children}
      </div>
    </div>
  );
}

function SessionChecking() {
  const { t } = useTranslation();

  return (
    <GateScreen>
      <Spinner size="lg" label={t('auth.gate.checking')} className="text-[var(--brand)]" />
    </GateScreen>
  );
}

/**
 * The check could not complete — offline, timed out, or the refresh endpoint
 * pushed back. We do not know whether there is a session, so this says exactly
 * that rather than guessing in either direction.
 *
 * `role="alert"`: it replaces a screen the user asked for, so it must be
 * announced rather than discovered.
 */
function SessionCheckFailed() {
  const { t } = useTranslation();
  const retry = useSessionStore((state) => state.retrySessionCheck);

  return (
    <GateScreen>
      <div role="alert" className="flex flex-col gap-2">
        <h1 className="text-title font-semibold">{t('auth.gate.failedTitle')}</h1>
        <p className="text-body text-[var(--text-secondary)]">{t('auth.gate.failedBody')}</p>
      </div>

      <div className="flex w-full flex-col gap-2.5 pt-2">
        <Button size="lg" shape="pill" fullWidth onClick={retry}>
          {t('common.retry')}
        </Button>
        <Link
          to={ROUTES.login}
          className={buttonClasses({
            variant: 'ghost',
            size: 'lg',
            shape: 'pill',
            fullWidth: true,
            className: 'bg-[var(--surface)]'
          })}
        >
          {t('auth.signIn')}
        </Link>
      </div>
    </GateScreen>
  );
}
